import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Eye, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { publicApi } from '../utils/api';
import StackConsultingPrime from '../components/StackConsultingPrime';
import SEOHead from '../components/SEOHead';
import { FEATURED_BLOG_POSTS_BY_SLUG } from '../data/editorialContent';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPost = useCallback(async () => {
    try {
      const response = await publicApi.getBlogPost(slug);
      setPost(response.data || FEATURED_BLOG_POSTS_BY_SLUG[slug] || null);
    } catch (error) {
      console.error('Failed to load post:', error);
      setPost(FEATURED_BLOG_POSTS_BY_SLUG[slug] || null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 page-bg-shell page-bg-devices flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-24 page-bg-shell page-bg-devices">
        <div className="container mx-auto px-6 py-12 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-cyber-yellow hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-devices" data-testid="blog-detail-page">
      <SEOHead
        pageType="blog"
        title={post.title}
        description={post.meta_description || post.excerpt || ''}
        slug={`blog/${post.slug}`}
        faqs={post.faqs || []}
        dateModified={post.updated_at || post.created_at}
        author={post.author || 'Wowrolla Editorial'}
        category={post.category || 'Blog'}
      />

      <article className="container mx-auto px-6 py-12 max-w-4xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Link to="/blog" className="text-text-secondary hover:text-cyber-yellow inline-flex items-center gap-2 mb-8 transition-colors">
            <ArrowLeft size={16} /> Back to Blog
          </Link>

          {post.cover_image && (
            <img src={post.cover_image} alt={post.title} className="w-full h-64 md:h-96 object-cover rounded-lg mb-8" />
          )}

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {post.title}
          </h1>

          <div className="terminal-grid p-4 mb-6" style={{ background: 'rgba(255,232,0,0.05)', borderLeft: '4px solid var(--accent-primary)' }} data-testid="blog-quick-answer">
            <p className="text-xs uppercase tracking-[0.14em] mb-2" style={{ color: 'var(--text-muted)' }}>Quick answer</p>
            <p style={{ color: 'var(--text-primary)' }}>{post.excerpt || post.meta_description || 'This guide summarizes the most important decision factors, tradeoffs, and implementation notes so you can choose faster with fewer regrets.'}</p>
          </div>

          <div className="flex items-center gap-4 text-sm text-text-muted mb-8 flex-wrap">
            <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(post.created_at).toLocaleDateString()}</span>
            <span className="flex items-center gap-1">Last updated {new Date(post.updated_at || post.created_at).toLocaleDateString()}</span>
            {post.views > 0 && <span className="flex items-center gap-1"><Eye size={14} /> {post.views} views</span>}
            {post.category && <span className="px-2 py-0.5 bg-cyber-yellow/10 text-cyber-yellow rounded-full text-xs">{post.category}</span>}
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="flex gap-2 mb-8 flex-wrap">
              {post.tags.map(tag => (
                <span key={tag} className="text-xs px-3 py-1 bg-white/5 text-text-secondary rounded-full flex items-center gap-1">
                  <Tag size={10} /> {tag}
                </span>
              ))}
            </div>
          )}

          {/* Blog Body - rendered as markdown-like content */}
          <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed">
            {post.body.split('\n').map((line, i) => {
              if (line.startsWith('### ')) return <h3 key={i} className="text-xl font-bold text-white mt-8 mb-3">{line.slice(4)}</h3>;
              if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold text-white mt-10 mb-4">{line.slice(3)}</h2>;
              if (line.startsWith('# ')) return <h1 key={i} className="text-3xl font-bold text-white mt-12 mb-4">{line.slice(2)}</h1>;
              if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-6 mb-1">{line.slice(2)}</li>;
              if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-bold text-white my-2">{line.slice(2, -2)}</p>;
              if (line.trim() === '') return <br key={i} />;
              return <p key={i} className="mb-3">{line}</p>;
            })}
          </div>
        </motion.div>

        <StackConsultingPrime />
      </article>
    </div>
  );
};

export default BlogDetailPage;

